import {
    List,
    ListItem,
    ListItemText,
    IconButton,
    FormControlLabel,
    Checkbox,
    Divider,
    Stack,
    TextField,
    Button,
    Typography
} from '@mui/material';
import { useEventContext, useGlobalContext, useQuizContext, useUserContext } from 'contexts';
import { useEffect, useState } from 'react';
import { NavUtils, NavPages, getRandomInt } from 'utils';
import { ShipLocation, Team, SharpshooterQuizSetup } from './sharpshooter-types';
import ClearIcon from '@mui/icons-material/Clear';
import { QuizQuestions } from 'graphql/queries/types/QuizQuestions';
import { letterByNumber } from './grid-generator';
import { useNavigate, useParams } from 'react-router-dom';

enum SetupType {
    Live,
    SinglePlayer,
    SessionSetup
}

interface Props {
    quizQuestions: QuizQuestions;
    onQuizStart?: (setup: SharpshooterQuizSetup) => void;
    onCreateQuizSession?: (setup: SharpshooterQuizSetup) => void;
}

/**
 * Battleship quiz setup compoment.
 * @param props
 * @returns
 */
export const SharpshooterSetup = (props: Props) => {
    const navigate = useNavigate();
    const { eventAlias } = useParams();
    const eventContext = useEventContext();
    const { quiz } = useQuizContext();
    const gContext = useGlobalContext();
    const { isQuizCreatorOrEventAdmin } = useUserContext();

    const [newTeamName, setNewTeamName] = useState<string>('');
    const [teams, setTeams] = useState<Team[]>([]);
    const [showShipLocations, setShowShipLocations] = useState(false);
    const [gridColumns, setGridColumns] = useState(7);
    const [gridRows, setGridRows] = useState(6);
    const [teamShipCount, setTeamShipCount] = useState(2);
    const [maxGridColumns, setMaxGridColumns] = useState(7);
    const [maxGridRows, setMaxGridRows] = useState(6);

    useEffect(() => {
        calculateRecommendedGrid(props.quizQuestions);
    }, [props.quizQuestions]);

    const calculateRecommendedGrid = (data: QuizQuestions) => {
        const questionCount = data.quiz_master_quizzes_by_pk?.questions.length!;
        const sqRoot = Math.sqrt(questionCount / 2);
        setGridRows(Math.floor(sqRoot));
        setGridColumns(Math.round(sqRoot));
        setMaxGridRows(Math.floor(questionCount / 2));
        setMaxGridColumns(Math.round(questionCount / 2));
    };

    const changeGridSize = (rows: number, columns: number) => {
        if (!props.quizQuestions) {
            return;
        }
        const questionCount = props.quizQuestions.quiz_master_quizzes_by_pk!.questions.length;
        if (questionCount < rows * columns) {
            alert('Grid size cannot be greater than the available number of questions!');
            return;
        }
        if (rows < 2 || columns < 2) {
            alert('Minimal grid dimention is 2!');
            return;
        }
        if (columns !== gridColumns) {
            setGridColumns(columns);
        }
        if (rows !== gridRows) {
            setGridRows(rows);
        }

        recalculateTeamShips();
    };

    const removeTeam = (teamName: string) => {
        setTeams(teams.filter((t) => t.name !== teamName));
    };

    const addNewTeam = () => {
        const newTeam: Team = {
            name: newTeamName,
            shipLocations: generateSafeShipLocationsForNewTeam(gridColumns, gridRows, teams, teamShipCount)
        };
        setTeams([...teams, newTeam]);
        setNewTeamName('');
    };

    const getAllShipLocations = (teams: Team[], excludeLocations?: ShipLocation[]): ShipLocation[] => {
        let allShips: ShipLocation[] = excludeLocations ?? [];
        teams.forEach((team) => {
            allShips = allShips.concat(team.shipLocations);
        });
        return allShips;
    };

    const shipLocationExists = (allShips: ShipLocation[], newShip: ShipLocation): boolean => {
        return !!allShips.find((ship) => ship.columnIndex === newShip.columnIndex && ship.rowIndex === newShip.rowIndex);
    };

    const generateSafeShipLocationsForNewTeam = (
        columns: number,
        rows: number,
        currentTeams: Team[],
        shipsPerTeam: number
    ): ShipLocation[] => {
        const allShips = getAllShipLocations(currentTeams);
        const newTeamShips: ShipLocation[] = [];
        let newShip: ShipLocation;

        do {
            const shipColumn = getRandomInt(0, columns - 1);
            const shipRow = getRandomInt(0, rows - 1);
            newShip = {
                columnIndex: shipColumn,
                rowIndex: shipRow,
                locationCode: `${letterByNumber(shipColumn)}${shipRow + 1}`
            };
            if (!shipLocationExists(allShips, newShip)) {
                newTeamShips.push(newShip);
                allShips.push(newShip);
            }
        } while (newTeamShips.length < shipsPerTeam);
        return newTeamShips;
    };

    const recalculateTeamShips = (shipCount?: number) => {
        const recalculatedTeams: Team[] = [];
        const shipsPerTeam = shipCount ?? teamShipCount;
        teams.forEach((t) => {
            recalculatedTeams.push({
                name: t.name,
                shipLocations: generateSafeShipLocationsForNewTeam(gridColumns, gridRows, recalculatedTeams, shipsPerTeam)
            });
        });
        setTeams(recalculatedTeams);
    };

    const onTeamShipCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const ships = parseInt(event.target.value);
        setTeamShipCount(ships);
        recalculateTeamShips(ships);
    };

    const onSetupComplete = (setupType: SetupType) => {
        const setup: SharpshooterQuizSetup = {
            columns: gridColumns,
            rows: gridRows,
            teams
        };

        switch (setupType) {
            case SetupType.Live:
                props.onQuizStart?.(setup);
                return;

            case SetupType.SessionSetup:
                props.onCreateQuizSession?.(setup);
                return;

            case SetupType.SinglePlayer:
            default:
                setup.isSinglePlayer = true;
                props.onQuizStart?.(setup);
                return;
        }
    };

    return (
        <div className="aq-content">
            <h2 className="quiz-title">{quiz.name}</h2>
            <div>
                <p className="quiz-description">{quiz.description}</p>
                <p className="quiz-genre">{quiz.genreId && <>Genre: {gContext.getGenreName(quiz.genreId)}</>}</p>
            </div>
            <br />
            {!!teams.length && (
                <>
                    <h4>Teams list</h4>
                    <List sx={{ width: '100%', maxWidth: 360 }}>
                        {teams.map((team, index) => (
                            <ListItem disablePadding key={`list-item-${index}`}>
                                <ListItemText
                                    key={`list-item-text-${index}`}
                                    primary={
                                        <>
                                            {`Team ${index + 1}:`}&nbsp;&nbsp;&nbsp;&nbsp;{team.name}
                                        </>
                                    }
                                    secondary={
                                        showShipLocations
                                            ? `Ship locations: ${team.shipLocations.map((sl) => sl.locationCode).join(', ')}`
                                            : ''
                                    }
                                />
                                <IconButton aria-label="delete team" onClick={() => removeTeam(team.name)}>
                                    <ClearIcon />
                                </IconButton>
                            </ListItem>
                        ))}
                    </List>
                    {isQuizCreatorOrEventAdmin(quiz.id!, eventAlias) && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    inputProps={{
                                        'aria-label': 'Show ship locations'
                                    }}
                                    checked={showShipLocations}
                                    onChange={() => setShowShipLocations(!showShipLocations)}
                                />
                            }
                            label="Show ship locations"
                        />
                    )}
                </>
            )}

            <Divider />
            <Stack spacing={2} direction="column" alignItems="center">
                <TextField label="Team name" value={newTeamName} onChange={(event) => setNewTeamName(event.target.value)} />

                <Button variant="contained" onClick={addNewTeam} disabled={!newTeamName}>
                    Add Team
                </Button>

                <Stack spacing={2} direction="row" justifyContent={'center'}>
                    <TextField
                        label="Number of columns"
                        type="number"
                        style={{ minWidth: '150px' }}
                        inputProps={{
                            min: 2,
                            max: maxGridColumns
                        }}
                        value={gridColumns}
                        onChange={(event) => changeGridSize(gridRows, parseInt(event.target.value))}
                    />
                    <TextField
                        label="Number of rows"
                        type="number"
                        style={{ minWidth: '150px' }}
                        inputProps={{
                            min: 2,
                            max: maxGridRows
                        }}
                        value={gridRows}
                        onChange={(event) => changeGridSize(parseInt(event.target.value), gridColumns)}
                    />
                </Stack>
                <TextField
                    label="Team ship count"
                    type="number"
                    style={{ minWidth: '150px', maxWidth: '250px' }}
                    inputProps={{
                        min: 2,
                        max: maxGridRows
                    }}
                    value={teamShipCount}
                    onChange={onTeamShipCountChange}
                />
                <Typography variant="caption">
                    Note: When changing grid size or number of ships per team, ship locations will be regenerated
                </Typography>

                {(quiz.publiclyAvailable || isQuizCreatorOrEventAdmin(quiz.id!)) && props.onQuizStart && (
                    <>
                        <Button variant="contained" onClick={() => onSetupComplete(SetupType.SinglePlayer)} disabled={!teams.length}>
                            Start Single Player Quiz
                        </Button>
                        <Button variant="contained" onClick={() => onSetupComplete(SetupType.Live)} disabled={!teams.length}>
                            Start Quiz
                        </Button>
                    </>
                )}
                {isQuizCreatorOrEventAdmin(quiz.id!) && (
                    <>
                        {props.onCreateQuizSession && (
                            <Button variant="contained" onClick={() => onSetupComplete(SetupType.SessionSetup)} disabled={!teams.length}>
                                Create Session
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            onClick={() => navigate(NavUtils.generateNavPath(NavPages.EditQuiz, quiz.id, eventContext?.alias))}
                        >
                            Edit Quiz
                        </Button>
                    </>
                )}
            </Stack>
        </div>
    );
};
